<template>
  <Layout>
    <div v-loading.fullscreen.lock="isLoading" element-loading-text="載入中...">
      <section class="drawer-layout Jost">
        <section class="drawer-layout__left">
          <ViewOrderLeftToolbarPartial
            :show-reschedule-order-btn="showRescheduleOrderBtn"
            :show-refund-btn="showRefundBtn"
            :show-settings-cancel-order-btn="showSettingsCancelOrderBtn"
            @back="onBackFromOrderDetail"
            @settings-action="onOrderSettingsClick"
          />
          <ViewOrderLeftContentPartial
            :member-info="currentMemberInfo"
            :accommodation-info="accommodationInfo"
            :pets-info="petsInfo"
            :order-note="selectedOrderNote"
            :order-status="selectedOrderStatus"
            :payment-status="selectedOrderPaymentStatus.paymentStatus"
            :checkout-signature-path="checkoutSignaturePath"
            :is-open-check-out-sign.sync="isOpenCheckOutSign"
            @update-member="fetchCurrentMember"
            @merge-member-info="onMergeMemberInfo"
            @accommodation-tab-change="onAccommodationTabChange"
            @pet-tab-click="onClickPetTab"
            @assessed-order="handleAssessedOrder"
            @check-in="onClickCheckIn"
            @open-checkout-sign="isOpenCheckOutSign = true"
            @save-checkout-signature="handleSaveCheckOutSignature"
            @view-check-in-form="onClickViewCheckInForm"
          />
        </section>

        <ViewOrderRightPanelPartial
          :visible="rightPanelView === 'order'"
          :title="selectedOrderTitle"
          :summary="selectedOrderSummary"
          :bookings="selectedOrderBookings"
          :subtotal="selectedOrderSubtotal"
          :status="selectedOrderStatus"
          :extra-guest-adjustments="selectedOrderExtraGuestAdjustments"
          :adjustments="selectedOrderAdjustments"
          :stay-nights="selectedOrderStayNights"
          :payment-info="paymentInfoDisplay"
          :invoice-info="invoiceInfoDisplay"
          :is-refunded="isRefunded"
        />

        <PetSidePanelPartial
          :visible.sync="isPetDetailDrawerShow"
          :pet-detail="selectedPetDetail"
          :initial-tab="petInfoActiveTab"
          :order-id="checkInFormOrderId"
          mode="view"
          @close="handleClosePetDetail"
          @updated="onPetInfoUpdated"
        />

        <OrderHistoryDrawerPartial :visible.sync="isOrderHistoryDrawerVisible" :histories="selectedOrderHistories" :order-display-id="selectedOrderDisplayId" />

        <BottomBar
          :currentStep="'viewOrder'"
          :totalPrice="selectedOrderTotal"
          :orderPaymentStatus="selectedOrderPaymentStatus"
          @go-to-payment="onClickGoToPayment"
          @open-notification-dialog="openNotificationDialog"
          @open-share-link-dialog="openShareLinkDialog"
        />
      </section>

      <ViewOrderReasonDialogsPartial
        :not-check-in-visible.sync="isNotCheckInDialogOpen"
        :not-check-in-reason.sync="notCheckedInReason"
        :cancel-visible.sync="isCancelReasonDialogOpen"
        :cancel-reason.sync="cancelReason"
        :refund-visible.sync="isRefundReasonDialogOpen"
        :refund-reason.sync="refundReason"
        @confirm-not-check-in="onClickConfirmNotCheckedIn"
        @confirm-cancel="onClickConfirmCancelReason"
        @dismiss-cancel="onClickDismissCancelReason"
        @confirm-refund="onClickConfirmRefundReason"
        @dismiss-refund="onClickDismissRefundReason"
      />

      <hotelCheckInForm
        v-model="isOpenCheckInForm"
        :mode="checkInFormMode"
        :product-id="checkInFormProductId"
        :container-id="checkInFormContainerId"
        :order-id="checkInFormOrderId"
        :stay-info="checkInFormStayInfo"
        @submit-check-in="fetchOrderDetail"
      />

      <OrderRescheduleDrawerPartial
        v-model="isOrderRescheduleDrawerVisible"
        :order-display-id="selectedOrderDisplayId"
        :product-id="productId"
        :container-id="containerId"
        :initial-bookings="selectedOrderInitialBookings"
        :order-guests="selectedOrderGuests"
        :room-title="selectedOrderSummary.roomTitle"
        @confirm="onRescheduleConfirm"
      />

      <NotificationEditor
        ref="notiEditor"
        :visible.sync="isNotificationDialogVisible"
        :display-info="notificationSummaryData"
        :loading="isNotificationSending"
        @confirm="handleSendNotification"
        :mode="'hotel'"
      />

      <SharePaymentLinkPartial
        :visible.sync="isShareLinkDialogVisible"
        :loading-sms="isTryingSendSMS"
        :data="shareLinkDefault"
        @share="handleShareLink"
        @send-sms="tryToSendSMS"
        @copy="copyShareLink"
      />
    </div>
  </Layout>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import { formatHotelCheckoutDisplay, formatHotelOrderStayRange, formatStayNightLabel, resolveHotelOrderStay, resolveHotelStay } from '@utils/format-hotel-stay'
import { buildOrderAdjustments, buildOrderBookings, buildOrderSummary, calcOrderSubtotal, calcOrderTotal, resolveStayNights } from '@utils/hotel-order-pricing'
import { mapOrdersToAccommodation, resolveOrdersList } from '@utils/hotel-order-accommodation-mapper'
import { buildInvoiceInfoDisplay, buildPaymentInfoDisplay } from '@utils/hotel-payment-display'
import formatDate from '@utils/format-date'
import { mapHotelRefundErrorMessage } from '@utils/hotel-refund-error'
import { mapHotelRescheduleErrorMessage } from '@utils/hotel-reschedule-error'
import { isSameBookingSelection, normalizeBookingDate } from '@utils/hotel-room-selection'

import Layout from '@layouts/main'
import hotelCheckInForm from './hotelCheckInForm.vue'
import PetSidePanelPartial from './partials/PetSidePanelPartial.vue'
import OrderHistoryDrawerPartial from './partials/OrderHistoryDrawerPartial.vue'
import OrderRescheduleDrawerPartial from './partials/OrderRescheduleDrawerPartial.vue'
import BottomBar from './partials/BottomBar.vue'
import NotificationEditor from './partials/NotificationEditorPartial'
import SharePaymentLinkPartial from './partials/SharePaymentLinkPartial'
import ViewOrderLeftToolbarPartial from './partials/ViewOrderLeftToolbarPartial.vue'
import ViewOrderLeftContentPartial from './partials/ViewOrderLeftContentPartial.vue'
import ViewOrderRightPanelPartial from './partials/ViewOrderRightPanelPartial.vue'
import ViewOrderReasonDialogsPartial from './partials/ViewOrderReasonDialogsPartial.vue'
import API from '../../api'
import { fetchMemberShortWithSummary, shouldUseLightMemberFetch } from '@utils/memberV2Fetch'
import { getMerchantInfo, getMerchantProductInfo } from '@/src/apis'
import { getHotelInfo } from '@/src/router/views/hotel/api'

export default {
  name: 'hotelViewOrder',
  components: {
    Layout,
    hotelCheckInForm,
    PetSidePanelPartial,
    OrderHistoryDrawerPartial,
    OrderRescheduleDrawerPartial,
    BottomBar,
    NotificationEditor,
    SharePaymentLinkPartial,
    ViewOrderLeftToolbarPartial,
    ViewOrderLeftContentPartial,
    ViewOrderRightPanelPartial,
    ViewOrderReasonDialogsPartial,
  },
  props: ['containerId', 'productId'],
  data() {
    return {
      isLoading: false,
      orderDetailed: null,
      rightPanelView: 'order',
      isPetDetailDrawerShow: false,
      isOrderHistoryDrawerVisible: false,
      isOrderRescheduleDrawerVisible: false,
      isNotCheckInDialogOpen: false,
      notCheckedInReason: '',
      isCancelReasonDialogOpen: false,
      cancelReason: '',
      isRefundReasonDialogOpen: false,
      refundReason: '',
      isOpenCheckInForm: false,
      checkInFormMode: 'edit',
      subOrders: [],
      selectedOrderIndex: 0,
      orderMeta: {},
      accommodationInfo: [],
      petsInfo: [],
      petDetailCache: {},
      petInfoActiveTab: 'information',
      petInfoTabs: [{ name: 'information', label: '寵物資訊' }, { name: 'service', label: '歷史紀錄' }, { name: 'currentService', label: '服務紀錄' }],
      petInfoLoading: false,
      selectedPetDetail: {},
      paymentInfo: {
        method: '',
        status: '',
        time: '',
      },
      invoiceInfo: {
        orderId: '',
        invoiceDate: '',
        invoiceNumber: '',
        randomCode: '',
        invoiceType: '',
        carrierType: '',
      },
      isOpenCheckOutSign: false,
      isNotificationDialogVisible: false,
      isNotificationSending: false,
      notificationSummaryData: [],
      isShareLinkDialogVisible: false,
      shareLinkDefault: {
        displayId: '',
        fullName: '',
        gender: 1,
        productId: '',
        items: '',
        uuid: '',
      },
      isTryingSendSMS: false,
    }
  },
  watch: {
    orderDetailed: {
      immediate: true,
      handler(order) {
        if (!order) return
        this.applyOrderData(order)
      },
    },
  },
  async created() {
    await this.fetchHotelSetting()
    await this.fetchOrderDetail()
  },
  computed: {
    selectedOrder() {
      return this.subOrders && this.subOrders.length ? this.subOrders[this.selectedOrderIndex] : null
    },
    selectedOrderHistories() {
      const histories = this.selectedOrder?.histories
      return Array.isArray(histories) ? histories : []
    },
    selectedOrderTitle() {
      const order = this.selectedOrder
      if (!order) return '訂單編號：-'
      return `訂單編號：${order.displayId || '-'}`
    },
    selectedOrderDisplayId() {
      const order = this.selectedOrder
      return order ? order.displayId || '' : ''
    },
    selectedOrderInitialBookings() {
      const order = this.selectedOrder
      return order && Array.isArray(order.bookings) ? order.bookings : []
    },
    selectedOrderGuests() {
      const order = this.selectedOrder
      return order && Array.isArray(order.guests) ? order.guests : []
    },
    selectedOrderBookings() {
      return buildOrderBookings(this.selectedOrder)
    },
    selectedOrderSummary() {
      return buildOrderSummary(this.selectedOrder, this.showCheckoutDate)
    },
    selectedOrderSubtotal() {
      return calcOrderSubtotal(this.selectedOrderBookings, this.selectedOrder?.extraGuestAdjustments)
    },
    selectedOrderStayNights() {
      return resolveStayNights(this.selectedOrderSummary, this.selectedOrder)
    },
    selectedOrderAdjustments() {
      return buildOrderAdjustments(this.selectedOrder, this.selectedOrderSummary?.nights)
    },
    selectedOrderTotal() {
      return calcOrderTotal(this.selectedOrderSubtotal, this.selectedOrderAdjustments, this.selectedOrderStayNights)
    },
    paymentInfoDisplay() {
      const payment = (this.orderMeta && this.orderMeta.payment) || null
      return buildPaymentInfoDisplay(payment, this.paymentInfo)
    },
    invoiceInfoDisplay() {
      const invoice = (this.orderMeta && this.orderMeta.invoice) || null
      const displayId = this.selectedOrder?.displayId
      return buildInvoiceInfoDisplay(invoice, displayId, this.invoiceInfo)
    },
    checkoutSignaturePath() {
      return this.selectedOrder && this.selectedOrder.signatures ? this.selectedOrder.signatures.CHECK_OUT : ''
    },
    checkInFormStayInfo() {
      const order = this.selectedOrder || {}
      const summary = this.selectedOrderSummary || {}
      const stay = resolveHotelOrderStay(order, this.showCheckoutDate)
      const ownerName = this.currentMemberInfo && this.currentMemberInfo.fullName ? this.currentMemberInfo.fullName : '-'
      const ownerPhone = this.currentMemberInfo && this.currentMemberInfo.phone ? this.currentMemberInfo.phone : '-'
      const amount = this.selectedOrderTotal != null ? `NT$${this.selectedOrderTotal.toLocaleString()}` : '-'
      const rawCheckOut = order.checkoutDate || order.endDate
      return {
        orderId: order.displayId || order.id || order.uuid || '-',
        ownerName,
        ownerPhone,
        checkInDate: stay?.checkIn || order.checkinDate || order.startDate || '-',
        checkOutDate: stay?.checkout || formatHotelCheckoutDisplay(rawCheckOut, this.showCheckoutDate) || rawCheckOut || '-',
        amount,
        roomTypes: summary.roomTitle || '-',
      }
    },
    selectedPetOrderValue() {
      const orderId = this.selectedOrder ? this.selectedOrder.id || this.selectedOrder.uuid : null
      const pet = this.selectedPetDetail || {}
      return {
        id: orderId,
        petId: pet.id || null,
        pet,
      }
    },
    selectedPetId() {
      if (this.selectedPetDetail && this.selectedPetDetail.id) return this.selectedPetDetail.id
      const order = this.selectedOrder
      if (order && order.pet && order.pet.id) return order.pet.id
      const guests = order && Array.isArray(order.guests) ? order.guests : []
      return guests.length ? guests[0].id : null
    },
    petCommentOrder() {
      const order = this.selectedOrder
      if (!order) return null
      const orderId = order.id || order.uuid || order.displayId || null
      if (!orderId || !this.selectedPetId) return null
      return {
        ...order,
        id: orderId,
        pet: {
          ...(order.pet || {}),
          id: this.selectedPetId,
        },
      }
    },
    checkInFormOrderId() {
      const order = this.selectedOrder
      if (!order) return null
      return order.uuid || order.id || order.orderId || null
    },
    checkInFormProductId() {
      const order = this.selectedOrder
      return (order && (order.productId || order.product_id)) || (this.orderDetailed && this.orderDetailed.productId) || null
    },
    checkInFormContainerId() {
      return (this.orderDetailed && (this.orderDetailed.containerId || this.orderDetailed.container_uuid)) || null
    },
    selectedOrderNote() {
      return this.selectedOrder?.note || ''
    },
    selectedOrderStatus() {
      return this.selectedOrder?.status || ''
    },
    selectedOrderPaymentStatus() {
      return this.orderMeta?.payment || {}
    },
    selectedOrderExtraGuestAdjustments() {
      return { extraGuestFee: this.selectedOrder?.extraGuestAdjustments || '' }
    },
    isTerminalOrderStatus() {
      return ['CHECK_OUT', 'CANCEL'].includes(this.selectedOrderStatus)
    },
    isBeforeCheckInStatus() {
      return ['UNPAID', 'PAID', 'NO_SHOW'].includes(this.selectedOrderStatus)
    },
    isPaidPayment() {
      return this.selectedOrderPaymentStatus.paymentStatus === 'PAID'
    },
    isRefunded() {
      return this.selectedOrder?.isRefunded === true
    },
    showRescheduleOrderBtn() {
      const payment = this.selectedOrderPaymentStatus.paymentStatus
      const status = this.selectedOrderStatus
      return payment === 'UNPAID' && ['UNPAID', 'PAID'].includes(status)
    },
    showSettingsCancelOrderBtn() {
      return ['UNPAID', 'PAID', 'CHECK_IN'].includes(this.selectedOrderStatus)
    },
    showRefundBtn() {
      return this.isPaidPayment && !this.isRefunded
    },

    ...mapGetters('merchant', ['uploadMerchantMedia']),
    ...mapGetters('hotel/order', ['currentMemberInfo']),
    ...mapGetters('hotel/setting', ['showCheckoutDate']),
    useLightMemberFetch() {
      return shouldUseLightMemberFetch(this.$route)
    },
  },
  methods: {
    ...mapMutations('hotel/setting', ['SET_SHOW_CHECKOUT_DATE']),
    async fetchHotelSetting() {
      try {
        const hotelInfo = await getHotelInfo()
        this.SET_SHOW_CHECKOUT_DATE(hotelInfo?.showCheckoutDate)
      } catch (error) {
        console.error('取得住宿設定失敗:', error)
        this.SET_SHOW_CHECKOUT_DATE(1)
      }
    },
    onBackFromOrderDetail() {
      if (this.$route.query.from === 'schedule-check-in') {
        this.$router.push({ name: 'schedule-check-in', hash: '#hotel' })
        return
      }
      this.$router.back()
    },
    onOrderSettingsClick(action) {
      if (action === 'cancel') {
        this.cancelReason = ''
        this.isCancelReasonDialogOpen = true
        return
      }
      if (action === 'history') {
        this.isOrderHistoryDrawerVisible = true
        return
      }
      if (action === 'reschedule') {
        this.isOrderRescheduleDrawerVisible = true
        return
      }
      if (action === 'refund') {
        this.refundReason = ''
        this.isRefundReasonDialogOpen = true
      }
    },
    formatRescheduleStayRange(bookings) {
      const stay = resolveHotelStay({ bookings, showCheckoutDate: this.showCheckoutDate })
      if (!stay) return '-'
      const checkIn = formatDate(stay.checkIn, 'date')
      const checkout = formatDate(stay.checkout, 'MM/DD')
      return `${checkIn} - ${checkout} (${formatStayNightLabel(stay.nights, this.showCheckoutDate)})`
    },
    formatRescheduleRoomTitle(selectedRooms) {
      const titles = (selectedRooms || []).map((item) => item?.type?.title).filter(Boolean)
      return Array.from(new Set(titles)).join('＆') || '-'
    },
    buildRescheduleBookingsPayload(selectedRooms) {
      return (selectedRooms || [])
        .map((b) => ({
          date: normalizeBookingDate(b.date),
          roomId: Number(b.roomId),
        }))
        .filter((b) => b.date && Number.isFinite(b.roomId))
    },
    async onRescheduleConfirm(selectedRooms) {
      const rooms = Array.isArray(selectedRooms) ? selectedRooms : []

      if (isSameBookingSelection(this.selectedOrderInitialBookings, rooms)) {
        this.$message.warning('日期與房型未變更，請重新選擇')
        return
      }

      const originalDate = this.formatRescheduleStayRange(this.selectedOrderInitialBookings)
      const originalRoom = this.selectedOrderSummary.roomTitle || '-'
      const newDate = this.formatRescheduleStayRange(rooms)
      const newRoom = this.formatRescheduleRoomTitle(rooms)

      const confirmMessage = [
        '<div class="reschedule-confirm__intro">是否確定修改此筆訂單的住宿日期？</div>',
        '<div class="reschedule-confirm__block">',
        '<div class="reschedule-confirm__label">【原預約】</div>',
        `<div class="reschedule-confirm__line">日期：${originalDate}</div>`,
        `<div class="reschedule-confirm__line">房型：${originalRoom}</div>`,
        '</div>',
        '<div class="reschedule-confirm__block">',
        '<div class="reschedule-confirm__label">【改期為】</div>',
        `<div class="reschedule-confirm__line">日期：${newDate}</div>`,
        `<div class="reschedule-confirm__line">房型：${newRoom}</div>`,
        '</div>',
      ].join('')

      try {
        await this.$confirm(confirmMessage, '確認修改日期？', {
          confirmButtonText: '確認修改',
          cancelButtonText: '暫時不用',
          dangerouslyUseHTMLString: true,
          center: true,
          showClose: false,
          customClass: 'half-width-confirm reschedule-confirm',
        })
        await this.processRescheduleLogic(rooms)
      } catch (err) {
        if (err !== 'cancel') console.error('修改日期失敗:', err)
      }
    },
    async processRescheduleLogic(selectedRooms) {
      if (this.isLoading) return
      if (!this.selectedOrder?.uuid) {
        this.$message.error('找不到訂單，請重新整理後再試')
        return
      }

      const bookings = this.buildRescheduleBookingsPayload(selectedRooms)
      if (!bookings.length) {
        this.$message.error('選擇的住宿資料不完整，請重新選擇')
        return
      }

      this.isLoading = true
      try {
        await API.order.postHotelOrderReschedule(this.productId, this.containerId, this.selectedOrder.uuid, {
          bookings,
        })
        await this.fetchOrderDetail()
        this.$message.success('已修改日期')
        this.isOrderRescheduleDrawerVisible = false
      } catch (err) {
        console.error(err)
        this.$message.error(mapHotelRescheduleErrorMessage(err?.status, err?.statusCode))
        throw err
      } finally {
        this.isLoading = false
      }
    },
    onClickDismissCancelReason() {
      this.isCancelReasonDialogOpen = false
      this.cancelReason = ''
    },
    async onClickConfirmCancelReason() {
      const confirmMessage = [
        '取消後將執行以下變更，請務必確認：',
        '',
        '<b>房源釋出與看板移除</b>',
        '該時段將重新開放預約，',
        '此筆資料亦會從住宿預約頁面消失且無法點擊',
        '',
        '<b>僅留存於訂單</b>',
        '後續僅能至「訂單管理」中查詢訂單資訊',
      ].join('<br>')

      try {
        await this.$confirm(confirmMessage, '⚠️ 確認取消此筆預約？', {
          confirmButtonText: '確認取消',
          cancelButtonText: '暫時不用',
          dangerouslyUseHTMLString: true,
          center: true,
          showClose: false,
          customClass: 'half-width-confirm cancel-order-confirm',
        })

        const reason = this.cancelReason
        await this.processCancelLogic(reason)
        this.isCancelReasonDialogOpen = false
        this.cancelReason = ''
      } catch (err) {
        if (err !== 'cancel') console.error('取消訂單失敗:', err)
        else {
          this.isCancelReasonDialogOpen = false
          this.cancelReason = ''
        }
      }
    },
    onClickDismissRefundReason() {
      this.isRefundReasonDialogOpen = false
      this.refundReason = ''
    },
    async onClickConfirmRefundReason() {
      const confirmMessage = [
        '請務必確認以下退款說明，送出後將無法復原：',
        '',
        '<b>僅支援全額退款</b>',
        '系統將依據訂單金額進行全額退款，不支援部分退款',
        '',
        '<b>操作不可逆</b>',
        '一旦執行退款，訂單將無法再次修改、取消重複申請',
      ].join('<br>')

      try {
        await this.$confirm(confirmMessage, '⚠️ 確認執行退款？', {
          confirmButtonText: '確認退款',
          cancelButtonText: '暫時不用',
          dangerouslyUseHTMLString: true,
          center: true,
          showClose: false,
          customClass: 'half-width-confirm refund-confirm',
        })

        const reason = this.refundReason
        await this.processRefundLogic(reason)
        this.isRefundReasonDialogOpen = false
        this.refundReason = ''
      } catch (err) {
        if (err !== 'cancel') {
          console.error('處理退款失敗:', err)
        }
        this.isRefundReasonDialogOpen = false
        this.refundReason = ''
      }
    },
    async processRefundLogic(reason = '') {
      this.isLoading = true
      try {
        const payment = this.selectedOrderPaymentStatus
        await API.order.postHotelOrderRefund(this.productId, this.containerId, this.selectedOrder.uuid, {
          refundDate: formatDate(new Date(), 'date-dash'),
          paymentMethod: payment.paymentMethod,
          ...(reason ? { note: reason } : {}),
        })
        await this.fetchOrderDetail()
        this.$message.success('已退款成功')
      } catch (err) {
        console.error(err)
        this.$message.error(mapHotelRefundErrorMessage(err?.status))
        throw err
      } finally {
        this.isLoading = false
      }
    },
    async fetchMemberById(memberId) {
      if (this.useLightMemberFetch) {
        return fetchMemberShortWithSummary(this.$store, memberId)
      }
      return this.$store.dispatch('member/fetchSingle', { memberId })
    },
    async fetchCurrentMember() {
      const memberId = this.currentMemberInfo && this.currentMemberInfo.id
      if (!memberId) return
      const member = await this.fetchMemberById(memberId)
      this.$store.commit('hotel/order/SET_MEMBER', member)
    },
    onMergeMemberInfo(member) {
      this.$store.commit('hotel/order/SET_MEMBER', member)
    },
    applyOrderData(order) {
      const payload = order
      const payloadData = payload && payload.data ? payload.data : {}
      const member = payloadData.member
      const payment = payloadData.payment || payload.payment || null
      const invoice = payloadData.invoice || payload.invoice || null
      this.orderMeta = { payment, invoice }
      if (member) {
        this.$store.commit('hotel/order/SET_MEMBER', member)
      }

      // API 回傳舊→新；畫面改為新→舊，預設選中最新一筆
      const ordersList = [...resolveOrdersList(payload)].reverse()
      if (!ordersList.length) return

      const { subOrders, accommodationInfo, petsInfo } = mapOrdersToAccommodation(ordersList, this.showCheckoutDate)
      this.subOrders = subOrders
      this.selectedOrderIndex = 0
      this.accommodationInfo = accommodationInfo
      this.petsInfo = petsInfo
    },
    onClickCheckIn() {
      const guests = this.selectedOrder.guests.map((item) => `<span style="color: #ea6a2a;">${item.name}</span>`).join('、')
      this.$confirm(`請問此訂單${guests}是否已報到`, '報到狀態', {
        confirmButtonText: '確認報到',
        cancelButtonText: '尚未報到',
        modalAppendToBody: false,
        showClose: false,
        center: true,
        dangerouslyUseHTMLString: true,
      })
        .then(() => {
          this.checkInFormMode = 'edit'
          this.isOpenCheckInForm = true
        })
        .catch((err) => {
          this.isNotCheckInDialogOpen = true
          console.log(err)
        })

      this.removeButtonFocus()
    },
    async onClickConfirmNotCheckedIn() {
      try {
        this.removeButtonFocus()

        await this.$confirm('確定將此修改為「未報到」狀態？', '警告', {
          confirmButtonText: '確定',
          cancelButtonText: '否',
          modalAppendToBody: false,
          showClose: false,
          center: true,
        })

        await API.order.postHotelOrderAction(this.productId, this.containerId, this.selectedOrder.uuid, { action: 'NO_SHOW', reason: this.notCheckedInReason })
        await this.fetchOrderDetail()
        this.$message.success('已成功修改為未報到狀態')
      } catch (err) {
        if (err !== 'cancel') {
          console.error('修改狀態失敗:', err)
          this.$message.error('操作失敗，請稍後再試')
        }
      } finally {
        this.isNotCheckInDialogOpen = false
        this.notCheckedInReason = ''
      }
    },
    onClickViewCheckInForm() {
      this.checkInFormMode = 'view'
      this.isOpenCheckInForm = true
    },
    onAccommodationTabChange(index) {
      const rawIndex = Number(index) || 0
      const maxIndex = this.subOrders.length ? this.subOrders.length - 1 : 0
      const idx = Math.min(Math.max(rawIndex, 0), maxIndex)
      this.petsInfo = this.accommodationInfo && this.accommodationInfo[idx] ? this.accommodationInfo[idx].pets || [] : []
      this.selectedOrderIndex = idx
      this.rightPanelView = 'order'
    },
    async onSelectPet(pet) {
      if (!pet || !pet.id) return
      const memberId = this.currentMemberInfo && this.currentMemberInfo.id
      let detail = this.petDetailCache[pet.id]

      if (!detail && memberId) {
        try {
          const { items } = await this.$store.dispatch('member/fetchSinglePet', { memberId, petId: pet.id })
          detail = items
          if (detail) {
            this.$set(this.petDetailCache, pet.id, detail)
          }
        } catch (err) {
          // ignore fetch errors for now
        }
      }

      this.selectedPetDetail = {
        ...pet,
        ...(detail || {}),
      }
    },
    async onClickPetTab({ pet, tab }) {
      await this.onSelectPet(pet)
      this.isPetDetailDrawerShow = true
      const nextTab = ['information', 'service', 'currentService'].includes(tab) ? tab : 'information'
      this.petInfoActiveTab = nextTab
    },
    handleClosePetDetail() {
      this.isPetDetailDrawerShow = false
    },
    async onPetInfoUpdated(payload = {}) {
      const petId = payload && payload.id ? payload.id : this.selectedPetDetail && this.selectedPetDetail.id
      if (petId) {
        this.$delete(this.petDetailCache, petId)
      }
      await this.onPetListUpdated()
    },
    onPetInfoDetail(val) {
      if (!val || !val.alias) return
      if (!this.selectedPetDetail || !this.selectedPetDetail.id) {
        const fallbackPet = Array.isArray(this.petsInfo) && this.petsInfo.length ? this.petsInfo[0] : null
        if (fallbackPet) {
          this.onSelectPet(fallbackPet)
        }
      }
      this.isPetDetailDrawerShow = true
      const nextTab = ['information', 'service', 'currentService'].includes(val.alias) ? val.alias : 'information'
      this.petInfoActiveTab = nextTab
    },
    async onPetListUpdated() {
      await this.fetchCurrentMember()
      if (this.selectedPetDetail && this.selectedPetDetail.id) {
        await this.onSelectPet(this.selectedPetDetail)
      }
    },
    onPetInfoLoadingChange(isLoading) {
      this.petInfoLoading = isLoading
    },
    async hydratePetsInfo(pets) {
      const memberId = this.currentMemberInfo && this.currentMemberInfo.id
      if (!memberId || !Array.isArray(pets) || !pets.length) return

      const petIds = Array.from(new Set(pets.map((pet) => pet.id).filter(Boolean)))
      const missingIds = petIds.filter((id) => !this.petDetailCache[id])

      if (missingIds.length) {
        const results = await Promise.all(
          missingIds.map((petId) =>
            this.$store
              .dispatch('member/fetchSinglePet', { memberId, petId })
              .then(({ items }) => ({ petId, items }))
              .catch(() => null)
          )
        )
        results.forEach((result) => {
          if (result && result.items) {
            this.$set(this.petDetailCache, result.petId, result.items)
          }
        })
      }

      this.petsInfo = pets.map((pet) => {
        const detail = this.petDetailCache[pet.id]
        if (!detail) return pet
        return {
          ...pet,
          ...detail,
          id: detail.id || pet.id,
          name: detail.name || pet.name,
          category: detail.categoryTitle || detail.category || pet.category,
          isAssessed: detail.isAssessed != null ? detail.isAssessed : pet.isAssessed,
          isEvaluationRequired: !!pet.isEvaluationRequired,
          imgUrl: detail.avatar || detail.imgUrl || pet.imgUrl,
        }
      })
    },
    async fetchOrderDetail() {
      this.isLoading = true
      try {
        const orderDetailed = await this.$store.dispatch('hotel/order/fetchOrder', {
          productId: this.productId,
          containerId: this.containerId,
        })
        const orderDetailedCopy = orderDetailed && typeof orderDetailed === 'object' ? JSON.parse(JSON.stringify(orderDetailed)) : orderDetailed
        const payloadData =
          orderDetailedCopy && typeof orderDetailedCopy === 'object' ? (orderDetailedCopy.data && typeof orderDetailedCopy.data === 'object' ? orderDetailedCopy.data : { ...orderDetailedCopy }) : {}
        const memberId = payloadData.member && payloadData.member.id
        if (memberId) {
          const member = await this.fetchMemberById(memberId)
          if (member) {
            payloadData.member = member
          }
        }
        if (orderDetailedCopy && typeof orderDetailedCopy === 'object') {
          if (!orderDetailedCopy.data || typeof orderDetailedCopy.data !== 'object') {
            orderDetailedCopy.data = payloadData
          }
          orderDetailedCopy.containerId = this.containerId
          orderDetailedCopy.productId = this.productId
        }
        console.log('Fetched order detail:', orderDetailedCopy)
        this.orderDetailed = orderDetailedCopy
      } catch (err) {
        this.$message({
          type: 'error',
          message: err,
        })
      } finally {
        this.isLoading = false
      }
    },
    async handleAssessedOrder(type) {
      const statusMap = {
        confirm: {
          label: '通過',
          title: '接受預約訂單',
          color: '#67C23A',
          handler: this.processConfirmLogic,
        },
        reject: {
          label: '不通過',
          title: '拒絕預約訂單',
          color: '#F56C6C',
          handler: this.processRejectLogic,
        },
      }

      const config = statusMap[type]
      if (!config) return

      try {
        this.$nextTick(() => {
          setTimeout(() => {
            if (document.activeElement) document.activeElement.blur()
          }, 50)
        })

        await this.$confirm(`評估寵物 <span style="color: ${config.color}; font-weight: bold;">${config.label}</span>`, config.title, {
          confirmButtonText: '是',
          cancelButtonText: '否',
          dangerouslyUseHTMLString: true,
          center: true,
          customClass: 'half-width-confirm',
        })

        if (typeof config.handler === 'function') {
          await config.handler()
        }
      } catch (err) {
        if (err !== 'cancel') console.error('發生錯誤:', err)
      }
    },
    async processConfirmLogic() {
      console.log('執行：通過審核、建立訂單、發送付款簡訊')
      this.isLoading = true
      try {
        console.log(this.productId, this.containerId, this.selectedOrder.uuid)
        await API.order.postHotelOrderAction(this.productId, this.containerId, this.selectedOrder.uuid, { action: 'CONFIRM', reason: '同意接單-未付款' })

        await this.fetchOrderDetail()
        this.$message.success('已確認訂單')
      } catch (err) {
        console.error(err)
        this.$message.error('確認訂單失敗，請稍後再嘗試一次')
      } finally {
        this.isLoading = false
      }
    },
    async processRejectLogic() {
      this.isLoading = true
      try {
        console.log(this.productId, this.containerId, this.selectedOrder.uuid)
        await API.order.postHotelOrderAction(this.productId, this.containerId, this.selectedOrder.uuid, { action: 'REJECT', reason: '拒絕接單' })
        await this.fetchOrderDetail()
        this.$message.success('已拒絕訂單')
      } catch (err) {
        console.error(err)
        this.$message.error('拒絕訂單失敗，請稍後再嘗試一次')
      } finally {
        this.isLoading = false
      }
    },
    async processCancelLogic(reason = '') {
      this.isLoading = true
      try {
        await API.order.postHotelOrderAction(this.productId, this.containerId, this.selectedOrder.uuid, {
          action: 'CANCEL',
          reason: reason || '',
        })
        await this.fetchOrderDetail()
        this.$message.success('已取消訂單')
      } catch (err) {
        console.error(err)
        this.$message.error('取消訂單失敗，請稍後再嘗試一次')
        throw err
      } finally {
        this.isLoading = false
      }
    },
    async onClickGoToPayment() {
      this.$nextTick(() => {
        setTimeout(() => {
          if (document.activeElement) document.activeElement.blur()
        }, 50)
      })
      try {
        await this.$confirm('', '是否確認前往付款頁面？', {
          confirmButtonText: '是',
          cancelButtonText: '否',
          center: true,
          customClass: 'half-width-confirm',
        })
        this.$router.push({
          name: 'PaymentMain',
          params: {
            source: 'hotel',
            containerId: this.containerId,
          },
          query: {
            productId: this.productId,
          },
        })
      } catch (err) {
        if (err !== 'cancel') console.error(err)
      }
    },
    async handleSaveCheckOutSignature(signatureData) {
      try {
        await this.$confirm('確定要執行退房流程嗎？', '退房確認', {
          confirmButtonText: '確定',
          cancelButtonText: '取消',
          center: true,
          customClass: 'half-width-confirm',
        })

        this.isLoading = true

        const uploadRes = await this.$store.dispatch('merchant/uploadMerchantMedia', signatureData)
        await this.fetchCheckOutAction(uploadRes)

        await this.fetchOrderDetail()
        this.$message.success('已退房成功')
      } catch (error) {
        if (error !== 'cancel') {
          console.error('退房流程失敗:', error)
          this.$message.error('退房處理失敗，請稍後再試')
        }
      } finally {
        this.isLoading = false
      }
    },
    async fetchCheckOutAction(signatureURL) {
      const payload = {
        action: 'CHECK_OUT',
        reason: '退房完成',
        signaturePath: signatureURL,
      }

      await API.order.postHotelOrderAction(this.productId, this.containerId, this.selectedOrder.uuid, payload)
    },
    removeButtonFocus() {
      this.$nextTick(() => {
        setTimeout(() => {
          if (document.activeElement) document.activeElement.blur()
        }, 50)
      })
    },
    async fetchMerchantInfo() {
      this.isLoading = true
      try {
        const res = await getMerchantInfo()
        return res
      } catch (error) {
        console.error(error)
      } finally {
        this.isLoading = false
      }
    },
    async fetchProductInfo() {
      this.isLoading = true
      try {
        const res = await getMerchantProductInfo(this.selectedOrder.productId)
        return res
      } catch (error) {
        console.error(error)
      } finally {
        this.isLoading = false
      }
    },
    async openNotificationDialog() {
      const merchantInfo = await this.fetchMerchantInfo()
      const productInfo = await this.fetchProductInfo()
      this.notificationSummaryData = [
        { label: '訂單編號', value: `#${this.selectedOrder?.displayId}` || '' },
        { label: '預約時間', value: this.selectedOrderSummary?.dateRange || '' },
        { label: '預約店家', value: merchantInfo.items.name || '' },
        { label: '預約分店', value: productInfo.title || '' },
        { label: '預約項目', value: '寵物住宿' || '' },
        { divider: true },
        { divider: true },
        { label: '客戶姓名', value: this.orderDetailed?.member?.fullName || '' },
        { label: '客戶電話', value: this.orderDetailed?.member?.phone || '' },
        { label: '電子信箱', value: this.orderDetailed?.member?.email || '' },
        { label: '其他備註', value: this.selectedOrder?.note || '' },
      ]

      this.isNotificationDialogVisible = true
    },
    async handleSendNotification(formData) {
      this.isNotificationSending = true
      try {
        console.log('準備打 API 的數據：', formData)
        const getRouterType = (router) => {
          if (router.includes('sms') && router.includes('email')) {
            return 'all'
          }
          return formData.router[0] || 'sms'
        }
        const payload = {
          mode: 'byHotel',
          hotelOrderId: this.selectedOrder?.uuid,
          templateContent: formData.templateContent,
          router: getRouterType(formData.router),
        }
        if (getRouterType(formData.router) === 'email' || getRouterType(formData.router) === 'all') {
          payload.subject = formData.subject && formData.subject.trim() !== '' ? formData.subject : '重要通知'
        }

        await this.$store.dispatch('merchant/sendNoti', payload)
        this.$message.success('發送成功')
        this.isNotificationDialogVisible = false

        if (this.$refs.notiEditor) {
          this.$refs.notiEditor.resetForm()
        }
      } catch (err) {
        console.error(err)
        this.$message.error('發送失敗')
      } finally {
        this.isNotificationSending = false
      }
    },
    openShareLinkDialog() {
      console.log(this.orderDetailed)
      console.log(this.selectedOrder)

      const stayRange = formatHotelOrderStayRange(this.selectedOrder, this.showCheckoutDate)
      const nightDetails = this.selectedOrder?.bookings?.map((item) => `${item.date} ${item.title}`).filter(Boolean) || []

      this.shareLinkDefault = {
        displayId: this.selectedOrder?.displayId || '',
        fullName: this.orderDetailed?.member?.fullName || '',
        gender: this.orderDetailed?.member?.gender || '',
        productId: this.selectedOrder?.productId || '',
        items: [stayRange, ...nightDetails].filter(Boolean).join('\n'),
        uuid: this.selectedOrder?.uuid || '',
      }
      this.isShareLinkDialogVisible = true
    },
    handleShareLink(platform, shortURL) {
      const platformLinkMap = {
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${shortURL}`,
        line: `https://social-plugins.line.me/lineit/share?url=${shortURL}`,
      }
      window.open(platformLinkMap[platform], '_blank')
    },
    async tryToSendSMS() {
      this.isTryingSendSMS = true
      try {
        console.log('發送簡訊中...')
        await this.$store.dispatch('order/sendContainerLink', {
          uuid: this.selectedOrder?.containerId,
          router: 'sms',
        })
        await new Promise((resolve) => setTimeout(resolve, 1500))
        this.$message.success('簡訊已發送')
      } finally {
        this.isTryingSendSMS = false
      }
    },
    copyShareLink(shortURL) {
      navigator.clipboard.writeText(shortURL).then(() => {
        this.$message.success('已複製連結至剪貼簿')
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import './styles/hotel-view-order.scss';
</style>
<style lang="scss">
@import './styles/hotel-view-order-global.scss';
</style>
